import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple";
const labelCls = "text-xs uppercase tracking-[0.15em] text-white/50 block mb-1.5";

export default function WebsiteAdmin() {
  const [s, setS] = useState(null);
  const [features, setFeatures] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });

  const load = () => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/features").then((r) => setFeatures(r.data)).catch(() => {});
    api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (patch, label = "Saved") => {
    try {
      await api.put("/admin/settings", patch);
      toast.success(label);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!s) return <p className="text-white/40 font-mono text-sm">Loading site settings…</p>;

  return (
    <div data-testid="admin-website">
      <PageTitle title="Website Management" sub="Edit what the public sees — hero, links, stats, sections, SEO." />
      <Tabs defaultValue="hero">
        <TabsList className="bg-panel border border-white/5 mb-6" data-testid="website-tabs">
          <TabsTrigger value="hero" data-testid="website-tab-hero">Homepage</TabsTrigger>
          <TabsTrigger value="stats" data-testid="website-tab-stats">Statistics</TabsTrigger>
          <TabsTrigger value="features" data-testid="website-tab-features">Features</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="website-tab-faqs">FAQ</TabsTrigger>
          <TabsTrigger value="seo" data-testid="website-tab-seo">SEO & Sections</TabsTrigger>
        </TabsList>

        <TabsContent value="hero">
          <div className="rounded-lg border border-white/5 bg-panel p-6 space-y-5 max-w-3xl" data-testid="website-hero-form">
            <div>
              <label className={labelCls}>Hero overline</label>
              <input value={s.hero_overline} onChange={(e) => setS({ ...s, hero_overline: e.target.value })} className={inputCls} data-testid="edit-hero-overline" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {s.hero_lines.map((line, i) => (
                <div key={i}>
                  <label className={labelCls}>Headline line {i + 1}</label>
                  <input value={line} onChange={(e) => { const l = [...s.hero_lines]; l[i] = e.target.value; setS({ ...s, hero_lines: l }); }} className={inputCls} data-testid={`edit-hero-line-${i}`} />
                </div>
              ))}
            </div>
            <div>
              <label className={labelCls}>Hero subtitle</label>
              <textarea value={s.hero_subtitle} onChange={(e) => setS({ ...s, hero_subtitle: e.target.value })} rows={3} className={`${inputCls} resize-none`} data-testid="edit-hero-subtitle" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Bot invite URL</label>
                <input value={s.bot_invite_url} onChange={(e) => setS({ ...s, bot_invite_url: e.target.value })} className={`${inputCls} font-mono text-xs`} data-testid="edit-invite-url" />
              </div>
              <div>
                <label className={labelCls}>Support server URL</label>
                <input value={s.support_invite_url} onChange={(e) => setS({ ...s, support_invite_url: e.target.value })} className={`${inputCls} font-mono text-xs`} data-testid="edit-support-url" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Logo image URL (leave empty for the built-in mark)</label>
              <input value={s.logo_url || ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })} className={`${inputCls} font-mono text-xs`} data-testid="edit-logo-url" />
            </div>
            <div className="rounded-md border border-white/10 p-4 flex items-center justify-between gap-4">
              <div className="flex-1">
                <label className={labelCls}>Announcement banner</label>
                <input value={s.announcement_banner.text} onChange={(e) => setS({ ...s, announcement_banner: { ...s.announcement_banner, text: e.target.value } })} className={inputCls} data-testid="edit-banner-text" />
              </div>
              <Switch checked={s.announcement_banner.enabled} onCheckedChange={(v) => setS({ ...s, announcement_banner: { ...s.announcement_banner, enabled: v } })} data-testid="edit-banner-toggle" />
            </div>
            <button onClick={() => save({ hero_overline: s.hero_overline, hero_lines: s.hero_lines, hero_subtitle: s.hero_subtitle, bot_invite_url: s.bot_invite_url, support_invite_url: s.support_invite_url, logo_url: s.logo_url, announcement_banner: s.announcement_banner }, "Homepage updated")}
              data-testid="save-hero" className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-8 py-3 rounded-full transition-colors duration-150">Save homepage</button>
          </div>
        </TabsContent>

        <TabsContent value="stats">
          <div className="rounded-lg border border-white/5 bg-panel p-6 max-w-2xl space-y-5" data-testid="website-stats-form">
            <p className="text-sm text-white/50">These numbers power the public hero and stats band.</p>
            <div className="grid grid-cols-2 gap-4">
              {[["servers", "Servers"], ["users", "Users"], ["songs_played", "Songs played"], ["uptime", "Uptime %"]].map(([k, label]) => (
                <div key={k}>
                  <label className={labelCls}>{label}</label>
                  <input type="number" step={k === "uptime" ? "0.1" : "1"} value={s.stats[k]} onChange={(e) => setS({ ...s, stats: { ...s.stats, [k]: Number(e.target.value) } })} className={inputCls} data-testid={`edit-stat-${k}`} />
                </div>
              ))}
            </div>
            <div>
              <label className={labelCls}>Live stats API URL (optional — overrides numbers above when reachable)</label>
              <input value={s.live_stats_url || ""} onChange={(e) => setS({ ...s, live_stats_url: e.target.value })} className={`${inputCls} font-mono text-xs`} placeholder="https://your-bot-api.example.com/stats" data-testid="edit-live-stats-url" />
            </div>
            <button onClick={async () => { try { await api.put("/admin/stats", s.stats); await save({ live_stats_url: s.live_stats_url }, "Statistics updated"); } catch (e) { toast.error("Failed"); } }}
              data-testid="save-stats" className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-8 py-3 rounded-full transition-colors duration-150">Save statistics</button>
          </div>
        </TabsContent>

        <TabsContent value="features">
          <div className="grid md:grid-cols-2 gap-3" data-testid="website-features-list">
            {features.map((f) => (
              <div key={f.id} className={`rounded-lg border p-5 ${f.enabled ? "border-white/5 bg-panel" : "border-white/5 bg-panel opacity-50"}`} data-testid={`feature-edit-${f.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <input value={f.title} onChange={(e) => setFeatures(features.map((x) => x.id === f.id ? { ...x, title: e.target.value } : x))}
                    className="bg-transparent font-heading font-bold text-white text-sm focus:outline-none border-b border-transparent focus:border-blurple/50 w-full mr-3" data-testid={`feature-title-${f.id}`} />
                  <Switch checked={f.enabled} onCheckedChange={async (v) => { await api.patch(`/admin/features/${f.id}`, { enabled: v }); load(); }} data-testid={`feature-toggle-${f.id}`} />
                </div>
                <textarea value={f.description} rows={2} onChange={(e) => setFeatures(features.map((x) => x.id === f.id ? { ...x, description: e.target.value } : x))}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white/70 focus:outline-none focus:ring-2 focus:ring-blurple resize-none" data-testid={`feature-desc-${f.id}`} />
                <button onClick={async () => { await api.patch(`/admin/features/${f.id}`, { title: f.title, description: f.description }); toast.success("Feature saved"); }}
                  className="mt-2 text-xs font-semibold text-volt hover:text-white transition-colors duration-150" data-testid={`feature-save-${f.id}`}>Save</button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="faqs">
          <div className="max-w-3xl space-y-3" data-testid="website-faqs-list">
            {faqs.map((f) => (
              <div key={f.id} className="rounded-lg border border-white/5 bg-panel p-5" data-testid={`faq-edit-${f.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <input value={f.question} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, question: e.target.value } : x))}
                    className="bg-transparent font-heading font-bold text-white text-sm focus:outline-none border-b border-transparent focus:border-blurple/50 w-full" data-testid={`faq-question-${f.id}`} />
                  <button onClick={async () => { if (window.confirm("Delete FAQ?")) { await api.delete(`/admin/faqs/${f.id}`); load(); } }} className="text-white/30 hover:text-red-400 transition-colors duration-150" data-testid={`faq-delete-${f.id}`} aria-label="Delete"><Trash2 size={14} /></button>
                </div>
                <textarea value={f.answer} rows={2} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, answer: e.target.value } : x))}
                  className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white/70 focus:outline-none focus:ring-2 focus:ring-blurple resize-none" data-testid={`faq-answer-${f.id}`} />
                <button onClick={async () => { await api.patch(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer }); toast.success("FAQ saved"); }}
                  className="mt-2 text-xs font-semibold text-volt hover:text-white transition-colors duration-150" data-testid={`faq-save-${f.id}`}>Save</button>
              </div>
            ))}
            <div className="rounded-lg border border-dashed border-white/15 p-5 space-y-3" data-testid="faq-new-form">
              <input value={newFaq.question} onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} placeholder="New question…" className={inputCls} data-testid="faq-new-question" />
              <textarea value={newFaq.answer} onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })} placeholder="Answer…" rows={2} className={`${inputCls} resize-none`} data-testid="faq-new-answer" />
              <button onClick={async () => { try { await api.post("/admin/faqs", newFaq); setNewFaq({ question: "", answer: "" }); toast.success("FAQ added"); load(); } catch (e) { toast.error("Fill both fields"); } }}
                className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-150" data-testid="faq-add">
                <Plus size={14} /> Add FAQ
              </button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo">
          <div className="rounded-lg border border-white/5 bg-panel p-6 max-w-2xl space-y-5" data-testid="website-seo-form">
            <div>
              <label className={labelCls}>SEO title</label>
              <input value={s.seo?.title || ""} onChange={(e) => setS({ ...s, seo: { ...s.seo, title: e.target.value } })} className={inputCls} data-testid="edit-seo-title" />
            </div>
            <div>
              <label className={labelCls}>SEO description</label>
              <textarea value={s.seo?.description || ""} onChange={(e) => setS({ ...s, seo: { ...s.seo, description: e.target.value } })} rows={2} className={`${inputCls} resize-none`} data-testid="edit-seo-description" />
            </div>
            <div>
              <p className={`${labelCls} mb-3`}>Homepage sections</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(s.sections || {}).map(([k, v]) => (
                  <label key={k} className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3 text-sm text-white/70 capitalize cursor-pointer" data-testid={`section-toggle-${k}`}>
                    {k}
                    <Switch checked={v} onCheckedChange={(val) => setS({ ...s, sections: { ...s.sections, [k]: val } })} />
                  </label>
                ))}
              </div>
            </div>
            <button onClick={() => save({ seo: s.seo, sections: s.sections }, "SEO & sections saved")}
              data-testid="save-seo" className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-8 py-3 rounded-full transition-colors duration-150">Save</button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
